import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { ArticleDto } from "../../../models/article/dto/article.dto";
import { CreateArticleInputDto } from "../../../models/article/dto/create-article.dto";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { GET_ARTICLES_QUERY_KEY } from "./useListArticles";

export type CreateArticleWithImageInput = {
    articleData: Omit<CreateArticleInputDto, 'imageUrl'>;
    imageFile?: File;
};

export const useCreateArticle = () => {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const { selectedOrganization } = useSelectedOrganization();

    return useMutation({
        mutationFn: (input: CreateArticleInputDto | CreateArticleWithImageInput) => {
            const organizationId = selectedOrganization?.id;
            if (!organizationId) {
                throw new Error('No organization selected');
            }
            // Check if it's the new format with image file
            if ('articleData' in input) {
                const formData = new FormData();
                const articleData = { ...input.articleData, organizationId };

                // Append all article data fields
                Object.entries(articleData).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        if (Array.isArray(value)) {
                            formData.append(key, JSON.stringify(value));
                        } else {
                            formData.append(key, value.toString());
                        }
                    }
                });

                // Append image file if provided
                if (input.imageFile) {
                    formData.append('image', input.imageFile);
                }

                return client.fetchApiWithFormData<ArticleDto>({
                    endpoint: 'articles/with-image',
                    method: 'POST',
                    formData
                });
            } else {
                // Legacy format - regular JSON request
                return client.fetchApi<ArticleDto, CreateArticleInputDto>({
                    endpoint: 'articles',
                    method: 'POST',
                    body: { ...input, organizationId }
                });
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GET_ARTICLES_QUERY_KEY(selectedOrganization?.id || '') });
        },
    });
};
